import axios from "axios";
import { getCsrfToken } from "../functions/common";
import ShowError from "../functions/show-error";
import { env, env_url } from "./env";
import jwt_decode from "jwt-decode";

const HttpServices = {
  getData,
  postData,
  postFormData,
  patchData,
  setupFetchInterceptor,
};

let alertCount = 0;

const api = axios.create({
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "Strict-Transport-Security": "max-age=65540 ; includeSubDomains",
    "Content-Security-Policy": `default-src 'self'`,
  },
});

function getData(url) {
  const urlBody = env.baseUrl + url;
  return api({ url: urlBody, method: "GET" });
}

function postData(url, payLoad) {
  const urlBody = env.baseUrl + url;
  return api({ url: urlBody, data: payLoad, method: "POST" });
}

function patchData(url) {
  const urlBody = env.baseUrl + url;
  return api({ url: urlBody, method: "PATCH" });
}

function postFormData(url, payLoad) {
  const headerProps = {
    "Content-Type": "multipart/form-data",
  };
  const urlBody = env.baseUrl + url;
  return api({
    url: urlBody,
    headers: headerProps,
    data: payLoad,
    method: "POST",
  });
}

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null, csrfToken = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve({ token, csrfToken });
    }
  });

  failedQueue = [];
};

const onRequest = async (config) => {
  if (config.headers) {
    const csrfToken = getCsrfToken();
    config.headers["X-CSRF-TOKEN"] = csrfToken;
    config.headers["X-XSRF-TOKEN"] = csrfToken;
    const token = localStorage.getItem("token");
    let refreshToken = localStorage.getItem("refreshToken");

    if (token && token !== "" && token !== "OTP Sent Successfully") {
      const decoded = jwt_decode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      const decodedRefreshToken = refreshToken
        ? jwt_decode(refreshToken)
        : null;
      const isRefreshTokenExpired = decodedRefreshToken
        ? decodedRefreshToken.exp * 1000 < Date.now()
        : true;

      if (
        !isRefreshTokenExpired &&
        isExpired &&
        !config.url.includes("/refresh")
      ) {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            config.headers["Authorization"] = `Bearer ${refreshToken}`;

            const response = await postData(env_url.validatetoken);

            const newToken = response.data.access_token;

            // Extract CSRF token from response body or headers
            const newCsrfToken =
              response.headers["x-csrf-token"] ||
              response.data.csrfToken ||
              getCsrfToken();

            // Store new tokens locally
            localStorage.setItem("token", newToken);

            // Update axios default headers with new tokens
            api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            if (newCsrfToken) {
              api.defaults.headers.common["X-CSRF-TOKEN"] = newCsrfToken;
              api.defaults.headers.common["X-XSRF-TOKEN"] = newCsrfToken;
            }

            config.headers["Authorization"] = `Bearer ${newToken}`;
            if (newCsrfToken) {
              config.headers["X-CSRF-TOKEN"] = newCsrfToken;
              config.headers["X-XSRF-TOKEN"] = newCsrfToken;
            }

            processQueue(null, newToken, newCsrfToken);

            return config;
          } catch (err) {
            processQueue(err, null);
            throw err;
          } finally {
            isRefreshing = false;
          }
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: ({ token, csrfToken }) => {
              config.headers["Authorization"] = `Bearer ${token}`;
              if (csrfToken) {
                config.headers["X-CSRF-TOKEN"] = csrfToken;
                config.headers["X-XSRF-TOKEN"] = csrfToken;
              }
              resolve(config);
            },
            reject: (err) => {
              reject(err);
            },
          });
        });
      } else if (isExpired && isRefreshTokenExpired) {
        localStorage.clear();
        setTimeout(() => {
          window.location.href = "/signin";
        }, 3000);
        if (alertCount === 0) {
          alert(
            "Your login session is over!! Please Login again to continue the application.."
          );
          alertCount++;
        }
      } else if (config.url.includes("/refresh")) {
        config.headers["Authorization"] = `Bearer ${refreshToken}`;
      } else {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
  }
  return config;
};

const onRequestError = (error) => {
  console.error(`[request error] [${JSON.stringify(error)}]`);
  if (error && error.response && error.response.status === 401) {
    setTimeout(() => {
      localStorage.clear();
      window.location.href = "/signin";
    }, 3000);
    if (alertCount === 0) {
      alert(
        "Your login session is over!! Please Login again to continue the application.."
      );
      alertCount++;
    }
  }
  return Promise.reject(error);
};

const onResponse = (response) => {
  // Update CSRF token if backend sends a fresh one in any response
  const newCsrfToken =
    response.headers["x-csrf-token"] || response.data.csrfToken;
  if (newCsrfToken) {
    api.defaults.headers.common["X-CSRF-TOKEN"] = newCsrfToken;
    api.defaults.headers.common["X-XSRF-TOKEN"] = newCsrfToken;
  }
  return response;
};

const onResponseError = async (error) => {
  console.error(`[response error] [${JSON.stringify(error)}]`);
  if (error && error.response && error.response.status === 401) {
    localStorage.clear();
    setTimeout(() => {
      window.location.href = "/signin";
    }, 3000);
    if (alertCount === 0) {
      alert(
        "Your login session is over!! Please Login again to continue the application.."
      );
      alertCount++;
    }
  } else if (
    error &&
    error.response &&
    [404, 500].includes(error.response.status)
  ) {
    return ShowError(error);
  }
  return Promise.reject(error);
};

function setupFetchInterceptor() {
  const originalFetch = window.fetch;
  let failedQueue = [];

  const processQueue = (error, token = null, csrfToken = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve({ token, csrfToken });
      }
    });
    failedQueue = [];
  };

  window.fetch = async (...args) => {
    const [resource, config = {}] = args;

    const token = localStorage.getItem("token");
    let refreshToken = localStorage.getItem("refreshToken");
    const csrfToken = getCsrfToken();
    const modifiedConfig = { ...config, headers: new Headers(config.headers) };
    // Set CSRF headers dynamically
    modifiedConfig.headers.set("X-CSRF-TOKEN", csrfToken);
    modifiedConfig.headers.set("X-XSRF-TOKEN", csrfToken);
    if (token && token !== "" && token !== "OTP Sent Successfully") {
      try {
        const decoded = jwt_decode(token);
        const isExpired = decoded.exp * 1000 < Date.now();

        const decodedRefresh = refreshToken ? jwt_decode(refreshToken) : null;
        const isRefreshTokenExpired = decodedRefresh
          ? decodedRefresh.exp * 1000 < Date.now()
          : true;

        if (
          !isRefreshTokenExpired &&
          isExpired &&
          !resource.includes("/refresh")
        ) {
          if (!isRefreshing) {
            isRefreshing = true;

            try {
              const refreshUrl = env.baseUrl + env_url.validatetoken;

              const refreshResponse = await originalFetch(refreshUrl, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                  "Content-Type": "application/json",
                  "Access-Control-Allow-Origin": "*",
                  "Strict-Transport-Security":
                    "max-age=65540 ; includeSubDomains",
                  "Content-Security-Policy": `default-src 'self'`,
                  "X-CSRF-TOKEN": csrfToken,
                  "X-XSRF-TOKEN": csrfToken,
                },
              });

              const refreshData = await refreshResponse.json();
              const newToken = refreshData.access_token;

              const newCsrfToken =
                refreshResponse.headers.get("x-csrf-token") ||
                refreshData.csrfToken ||
                getCsrfToken();

              localStorage.setItem("token", newToken);
              api.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${newToken}`;
              if (newCsrfToken) {
                api.defaults.headers.common["X-CSRF-TOKEN"] = newCsrfToken;
                api.defaults.headers.common["X-XSRF-TOKEN"] = newCsrfToken;
              }

              processQueue(null, newToken, newCsrfToken);

              modifiedConfig.headers.set("Authorization", `Bearer ${newToken}`);
              if (newCsrfToken) {
                modifiedConfig.headers.set("X-CSRF-TOKEN", newCsrfToken);
                modifiedConfig.headers.set("X-XSRF-TOKEN", newCsrfToken);
              }

              return await originalFetch(resource, modifiedConfig);
            } catch (err) {
              processQueue(err, null);
              throw err;
            } finally {
              isRefreshing = false;
            }
          }

          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: ({ token, csrfToken }) => {
                modifiedConfig.headers.set("Authorization", `Bearer ${token}`);
                if (csrfToken) {
                  modifiedConfig.headers.set("X-CSRF-TOKEN", csrfToken);
                  modifiedConfig.headers.set("X-XSRF-TOKEN", csrfToken);
                }
                resolve(originalFetch(resource, modifiedConfig));
              },
              reject: (err) => reject(err),
            });
          });
        } else if (isExpired && isRefreshTokenExpired) {
          localStorage.clear();
          setTimeout(() => {
            window.location.href = "/signin";
          }, 3000);
          if (alertCount === 0) {
            alert("Your login session is over! Please login again.");
            alertCount++;
          }

          return Promise.reject(new Error("Session expired"));
        } else if (resource.includes("/refresh")) {
          modifiedConfig.headers.set("Authorization", `Bearer ${refreshToken}`);
        } else {
          modifiedConfig.headers.set("Authorization", `Bearer ${token}`);
        }
      } catch (err) {
        console.error("Token decode error:", err);
      }
    }

    const response = await originalFetch(resource, modifiedConfig);

    if (response.status === 401 && alertCount === 0) {
      localStorage.clear();
      setTimeout(() => {
        window.location.href = "/signin";
      }, 3000);
      alert("Your login session is over! Please login again.");
    }

    return response;
  };
}

api.interceptors.request.use(onRequest, onRequestError);
api.interceptors.response.use(onResponse, onResponseError);

export default HttpServices;
